import { type DependencyList, useEffect, useState } from 'react';

import { isEmpty, uniq } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetPositionList, doGetQuoteList } from 'src/actions';
import { GetQuoteListResponseDto, QuoteDto } from 'src/dtos';
import { Position } from 'src/models';
import { Maybe } from 'src/utils';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';

interface UsePositionsReturnType {
  positionList: Position[];
  tradablePositionList: Position[];
  nonTradablePositionList: Position[];
  isPositionListLoading: boolean;
  hasPositionListError: boolean;
  positionListError?: string;
  isPositionListFetched: boolean;
}

export interface UsePositionsOptions extends CustomHookBaseOptions {
  skipQuotes?: boolean;
  dependency?: DependencyList;
}

const EMPTY_POSITIONS: Position[] = [];

export const usePositions = (
  { skip, skipQuotes, dependency }: UsePositionsOptions = { skip: false, skipQuotes: false, dependency: [] },
): UsePositionsReturnType => {
  const dispatch = useDispatch();

  const positionList: Maybe<Position[]> = useSelector((state: any) => state.positions.list.data?.data);
  const isPositionListLoading: boolean = useSelector((state: any) => Boolean(state.positions.list?.isLoading));
  const hasPositionListFailed: boolean = useSelector((state: any) => Boolean(state.positions.list?.__failed));
  const positionsError: string | undefined = useSelector((state: any) => state.positions.list?.message);
  const isFetched: boolean = useSelector((state: any) => Boolean(state.positions.list?.__isFetched));
  const isQuoteListFetched: boolean = useSelector((state: any) => Boolean(state.quotes.list?.__isFetched));
  const quoteListData: Maybe<GetQuoteListResponseDto> = useSelector((state: any) => state.quotes.list.data);
  const hasQuoteListError: boolean = useSelector((state: any) => Boolean(state.quotes.list?.__failed));
  const [tradableList, setTradableList] = useState<Position[]>([]);
  const [nonTradableList, setNonTradableList] = useState<Position[]>([]);
  const [tradableListWithQuotes, setTradableListWithQuotes] = useState<Position[]>([]);

  const { account } = useAccountSelector();

  useEffect(() => {
    if (isEmpty(quoteListData)) {
      return;
    }

    const securitiesList: Position[] = tradableList.map((position: Position) => {
      const quoteSecurityData = quoteListData?.quotedata.find((data: QuoteDto) => data.symbol === position.symbol);
      const security: Position = {
        ...position,
        value: quoteSecurityData?.pricedata?.last || 0,
        totalValue: position.quantity * (quoteSecurityData?.pricedata?.last || 0),
        change: quoteSecurityData?.pricedata?.changepercent,
      };

      if (quoteSecurityData?.longname) {
        security.description = quoteSecurityData?.longname;
      }

      return security;
    });
    setTradableListWithQuotes(securitiesList);
  }, [quoteListData, tradableList]);

  useEffect(() => {
    if (skipQuotes) {
      return;
    }

    if (isEmpty(tradableList)) {
      return;
    }

    if (isQuoteListFetched && !hasQuoteListError) {
      return;
    }

    const symbols = tradableList.map(aTradablePosition => aTradablePosition.symbol);
    dispatch(doGetQuoteList({ queryParams: { symbols: uniq(symbols).join(',') } }));
  }, [skipQuotes, tradableList]);

  useEffect(() => {
    if (positionList) {
      setTradableList(positionList.filter(position => position.isTradable));
      setTradableListWithQuotes(positionList.filter(position => position.isTradable));
      setNonTradableList(positionList.filter(position => !position.isTradable));
    }
  }, [positionList]);

  useEffect(() => {
    if (!positionList && !isPositionListLoading && !skip && !hasPositionListFailed && Boolean(account?.accountId)) {
      dispatch(doGetPositionList({ params: { accountId: account?.accountId as number } }));
    }
  }, dependency);

  return {
    positionList: Array.isArray(positionList) ? positionList : EMPTY_POSITIONS,
    isPositionListLoading,
    hasPositionListError: hasPositionListFailed || hasQuoteListError, // TODO: separate quote list error and position list error
    positionListError: positionsError,
    isPositionListFetched: isFetched,
    tradablePositionList: tradableListWithQuotes,
    nonTradablePositionList: nonTradableList,
  };
};
